import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { 
  Calendar as CalendarIcon,
  Clock,
  Phone,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  User,
  MessageCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format, isToday, isTomorrow, isPast } from "date-fns";

const API = process.env.REACT_APP_BACKEND_URL;

export default function FollowUpsPage({ user, logout }) {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterStatus, setFilterStatus] = useState("pending");
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completionNotes, setCompletionNotes] = useState("");
  const [completionDisposition, setCompletionDisposition] = useState("");
  const [stats, setStats] = useState({ pending: 0, completed: 0, missed: 0 });
  
  // Re-follow-up scheduling state
  const [scheduleReFollowUp, setScheduleReFollowUp] = useState(false);
  const [reFollowUpDate, setReFollowUpDate] = useState(null);
  const [reFollowUpTime, setReFollowUpTime] = useState("10:00");
  const [reFollowUpReason, setReFollowUpReason] = useState("");
  
  // Lead details modal state
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newDisposition, setNewDisposition] = useState("");
  const [dispositionNotes, setDispositionNotes] = useState("");
  
  // New Follow-up state (from Lead Modal)
  const [newFollowUpDate, setNewFollowUpDate] = useState(null);
  const [newFollowUpTime, setNewFollowUpTime] = useState("10:00");
  const [newFollowUpReason, setNewFollowUpReason] = useState("");
  
  // Reschedule modal state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState("10:00");
  const [rescheduleReason, setRescheduleReason] = useState("");
  const [followUpToReschedule, setFollowUpToReschedule] = useState(null);
  
  const [dispositions] = useState([
    { value: "not_contacted", label: "Not Contacted" },
    { value: "call_not_picked", label: "Call Not Picked" },
    { value: "callback_requested", label: "Callback Requested" },
    { value: "interested", label: "Interested - Follow Up" },
    { value: "not_interested", label: "Not Interested" },
    { value: "converted", label: "Converted" },
    { value: "no_service_zone", label: "No Service Zone" },
    { value: "hot_lead", label: "Hot Lead" },
    { value: "warm_lead", label: "Warm Lead" },
    { value: "cold_lead", label: "Cold Lead" },
    { value: "follow_up_1_3_months", label: "Follow Up (1-3 Months)" },
    { value: "follow_up_3_6_months", label: "Follow Up (3-6 Months)" },
    { value: "long_term", label: "Long Term (6+ Months)" },
    { value: "appointment_booked", label: "Appointment Booked" },
    { value: "busy", label: "Busy - Try Again" },
    { value: "call_later", label: "Call Later" },
    { value: "language_barrier", label: "Language Barrier" },
    { value: "wrong_number", label: "Wrong Number" },
    { value: "dnd", label: "DND (Do Not Disturb)" },
    { value: "voicemail", label: "Voicemail Left" },
    { value: "already_customer", label: "Already a Customer" },
  ]);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchFollowUps();
  }, [selectedDate, filterStatus]);

  const fetchFollowUps = async () => {
    try {
      setLoading(true);
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      let url = `${API}/api/followups?date=${dateStr}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setFollowUps(response.data.followups || []);
      
      // Calculate stats
      const all = response.data.followups || [];
      setStats({
        pending: all.filter(f => f.status === "pending").length,
        completed: all.filter(f => f.status === "completed").length,
        missed: all.filter(f => f.status === "missed").length
      });
    } catch (error) {
      console.error("Failed to fetch follow-ups");
    } finally {
      setLoading(false);
    }
  };

  const completeFollowUp = async (status) => {
    if (!selectedFollowUp) return;
    
    try {
      // Update current follow-up status with disposition
      await axios.put(`${API}/api/followups/${selectedFollowUp.followup_id}`, {
        status: status,
        notes: completionNotes,
        disposition: completionDisposition
      }, { withCredentials: true });
      
      // If re-follow-up is scheduled, create a new follow-up
      if (scheduleReFollowUp && reFollowUpDate) {
        const newFollowUp = {
          lead_id: selectedFollowUp.lead_id,
          scheduled_date: format(reFollowUpDate, "yyyy-MM-dd"),
          scheduled_time: reFollowUpTime,
          reason: reFollowUpReason || `Re-follow-up (Previous: ${completionDisposition || status})`
        };
        
        await axios.post(`${API}/api/followups`, newFollowUp, { withCredentials: true });
        toast.success(`Follow-up completed & new follow-up scheduled for ${format(reFollowUpDate, "dd MMM yyyy")}`);
      } else {
        toast.success(`Follow-up marked as ${completionDisposition || status}`);
      }
      
      // Reset all state
      setShowCompleteModal(false);
      setCompletionNotes("");
      setCompletionDisposition("");
      setScheduleReFollowUp(false);
      setReFollowUpDate(null);
      setReFollowUpTime("10:00");
      setReFollowUpReason("");
      fetchFollowUps();
    } catch (error) {
      toast.error("Failed to update follow-up");
    }
  };

  // Quick re-schedule from lead modal
  const scheduleFollowUpFromModal = async () => {
    if (!selectedLead || !reFollowUpDate) {
      toast.error("Please select a date");
      return;
    }
    
    try {
      const newFollowUp = {
        lead_id: selectedLead.lead_id,
        scheduled_date: format(reFollowUpDate, "yyyy-MM-dd"),
        scheduled_time: reFollowUpTime,
        reason: reFollowUpReason || "Follow-up scheduled"
      };
      
      await axios.post(`${API}/api/followups`, newFollowUp, { withCredentials: true });
      toast.success(`Follow-up scheduled for ${format(reFollowUpDate, "dd MMM yyyy")} at ${reFollowUpTime}`);
      
      // Reset
      setReFollowUpDate(null);
      setReFollowUpTime("10:00");
      setReFollowUpReason("");
      fetchFollowUps();
    } catch (error) {
      toast.error("Failed to schedule follow-up");
    }
  };

  // Open lead details modal
  const openLeadModal = async (followUp) => {
    setLeadLoading(true);
    setShowLeadModal(true);
    try {
      const response = await axios.get(`${API}/api/leads/${followUp.lead_id}`, { withCredentials: true });
      setSelectedLead(response.data);
      setNewDisposition(response.data.disposition || "not_contacted");
      fetchActivities(followUp.lead_id);
    } catch (error) {
      toast.error("Failed to load lead details");
      setShowLeadModal(false);
    } finally {
      setLeadLoading(false);
    }
  };

  // Fetch activities for a lead
  const fetchActivities = async (leadId) => {
    try {
      const response = await axios.get(`${API}/api/leads/${leadId}/activities`, { withCredentials: true });
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error("Failed to fetch activities");
    }
  };

  // Add note to lead
  const addNote = async () => {
    if (!selectedLead || !newNote.trim()) return;
    try {
      await axios.post(`${API}/api/leads/${selectedLead.lead_id}/notes`, {
        text: newNote
      }, { withCredentials: true });
      toast.success("Note added");
      setNewNote("");
      fetchActivities(selectedLead.lead_id);
      // Refresh lead data to show the new note
      const response = await axios.get(`${API}/api/leads/${selectedLead.lead_id}`, { withCredentials: true });
      setSelectedLead(response.data);
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  // Update disposition
  const updateDisposition = async () => {
    if (!selectedLead || !newDisposition) return;
    try {
      await axios.put(`${API}/api/leads/${selectedLead.lead_id}/disposition`, {
        disposition: newDisposition,
        notes: dispositionNotes
      }, { withCredentials: true });
      toast.success("Disposition updated");
      setDispositionNotes("");
      setSelectedLead(prev => ({ ...prev, disposition: newDisposition }));
      fetchActivities(selectedLead.lead_id);
    } catch (error) {
      toast.error("Failed to update disposition");
    }
  };

  // Reschedule follow-up
  const rescheduleFollowUp = async () => {
    if (!followUpToReschedule || !rescheduleDate) {
      toast.error("Please select a new date");
      return;
    }
    try {
      await axios.patch(`${API}/api/followups/${followUpToReschedule.followup_id}/reschedule`, {
        scheduled_date: format(rescheduleDate, "yyyy-MM-dd"),
        scheduled_time: rescheduleTime,
        reason: rescheduleReason
      }, { withCredentials: true });
      toast.success(`Follow-up rescheduled to ${format(rescheduleDate, "dd MMM yyyy")}`);
      setShowRescheduleModal(false);
      setFollowUpToReschedule(null);
      setRescheduleDate(null);
      setRescheduleTime("10:00");
      setRescheduleReason("");
      fetchFollowUps();
    } catch (error) {
      toast.error("Failed to reschedule follow-up");
    }
  };

  // Open reschedule modal
  const openRescheduleModal = (followUp) => {
    setFollowUpToReschedule(followUp);
    setRescheduleDate(new Date(followUp.scheduled_date));
    setRescheduleTime(followUp.scheduled_time || "10:00");
    setShowRescheduleModal(true);
  };

  // Schedule new follow-up from Lead Modal
  const scheduleNewFollowUp = async () => {
    if (!selectedLead || !newFollowUpDate) return;
    
    try {
      await axios.post(`${API}/api/followups`, {
        lead_id: selectedLead.lead_id,
        scheduled_date: format(newFollowUpDate, "yyyy-MM-dd"),
        scheduled_time: newFollowUpTime,
        reason: newFollowUpReason || "Manual follow-up scheduled"
      }, { withCredentials: true });
      
      toast.success(`Follow-up scheduled for ${format(newFollowUpDate, "dd MMM yyyy")} at ${newFollowUpTime}`);
      setNewFollowUpDate(null);
      setNewFollowUpTime("10:00");
      setNewFollowUpReason("");
      fetchFollowUps();
    } catch (error) {
      toast.error("Failed to schedule follow-up");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case "completed":
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case "missed":
        return <Badge className="bg-red-100 text-red-800">Missed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getDateLabel = (date) => {
    if (isToday(date)) return "Today";
    if (isTomorrow(date)) return "Tomorrow";
    return format(date, "EEEE, MMM dd");
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Follow-ups</h1>
            <p className="text-slate-600 mt-1">Manage your scheduled follow-up calls</p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchFollowUps}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Calendar Sidebar */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Select Date</CardTitle>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                className="rounded-md border"
              />
            </CardContent>
          </Card>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card 
                className={`cursor-pointer transition-all ${filterStatus === "pending" ? "ring-2 ring-yellow-500" : ""}`}
                onClick={() => setFilterStatus("pending")}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Pending</p>
                      <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                    </div>
                    <AlertCircle className="w-8 h-8 text-yellow-500" />
                  </div>
                </CardContent>
              </Card>
              <Card 
                className={`cursor-pointer transition-all ${filterStatus === "completed" ? "ring-2 ring-green-500" : ""}`}
                onClick={() => setFilterStatus("completed")}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Completed</p>
                      <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              <Card 
                className={`cursor-pointer transition-all ${filterStatus === "missed" ? "ring-2 ring-red-500" : ""}`}
                onClick={() => setFilterStatus("missed")}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Missed</p>
                      <p className="text-2xl font-bold text-red-600">{stats.missed}</p>
                    </div>
                    <XCircle className="w-8 h-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search + Date Header */}
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-xl font-semibold text-slate-900">
                {getDateLabel(selectedDate)}
              </h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by phone number..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-[220px] h-9 text-sm"
                    data-testid="followup-search"
                  />
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => { setFilterStatus(""); setSearchQuery(""); }}
                >
                  Show All
                </Button>
              </div>
            </div>

            {/* Follow-ups List */}
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : followUps.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No follow-ups scheduled for this date</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {followUps
                      .filter(f => !searchQuery || 
                        (f.lead_phone && f.lead_phone.includes(searchQuery)) ||
                        (f.lead_name && f.lead_name.toLowerCase().includes(searchQuery.toLowerCase()))
                      )
                      .map((followUp) => (
                      <div 
                        key={followUp.followup_id}
                        className="p-4 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div 
                            className="flex items-start gap-4 cursor-pointer"
                            onClick={() => openLeadModal(followUp)}
                            data-testid={`followup-lead-${followUp.followup_id}`}
                          >
                            <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                              <User className="w-6 h-6 text-cyan-700" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-slate-900 hover:text-cyan-700 hover:underline">{followUp.lead_name}</h3>
                              <p className="text-sm text-slate-500 flex items-center gap-1 hover:text-cyan-600">
                                <Phone className="w-3 h-3" />
                                {followUp.lead_phone}
                              </p>
                              <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                                <Clock className="w-3 h-3" />
                                {followUp.scheduled_time}
                              </p>
                              {followUp.reason && (
                                <p className="text-sm text-cyan-700 mt-1">{followUp.reason}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {getStatusBadge(followUp.status)}
                            {followUp.status === "pending" && (
                              <div className="flex gap-2 mt-2">
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => {
                                    setSelectedFollowUp(followUp);
                                    setShowCompleteModal(true);
                                  }}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Complete
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openRescheduleModal(followUp)}
                                >
                                  <CalendarIcon className="w-4 h-4 mr-1" />
                                  Reschedule
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600"
                                  onClick={() => {
                                    setSelectedFollowUp(followUp);
                                    completeFollowUp("missed");
                                  }}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                            {followUp.completion_notes && (
                              <p className="text-xs text-slate-500 mt-2 max-w-[200px] text-right">
                                {followUp.completion_notes}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Complete Follow-up Modal */}
        <Dialog open={showCompleteModal} onOpenChange={(open) => {
          setShowCompleteModal(open);
          if (!open) {
            setScheduleReFollowUp(false);
            setReFollowUpDate(null);
            setReFollowUpTime("10:00");
            setReFollowUpReason("");
            setCompletionDisposition("");
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Complete Follow-up</DialogTitle>
              <DialogDescription>
                Mark this follow-up and optionally schedule the next one
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="font-medium">{selectedFollowUp?.lead_name}</p>
                <p className="text-sm text-slate-500">{selectedFollowUp?.lead_phone}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Scheduled: {selectedFollowUp?.scheduled_date} at {selectedFollowUp?.scheduled_time}
                </p>
              </div>
              
              {/* Disposition Selection */}
              <div>
                <label className="text-sm font-medium text-slate-700">Call Result / Disposition</label>
                <Select value={completionDisposition} onValueChange={setCompletionDisposition}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select call result..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="connected_interested">Connected - Interested</SelectItem>
                    <SelectItem value="connected_callback">Connected - Call Back Later</SelectItem>
                    <SelectItem value="connected_not_interested">Connected - Not Interested</SelectItem>
                    <SelectItem value="connected_already_using">Connected - Already Using Hearing Aid</SelectItem>
                    <SelectItem value="no_answer">No Answer</SelectItem>
                    <SelectItem value="busy">Busy</SelectItem>
                    <SelectItem value="wrong_number">Wrong Number</SelectItem>
                    <SelectItem value="appointment_booked">Appointment Booked</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Textarea
                placeholder="Add notes about the call..."
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                rows={3}
              />

              {/* Re-Follow-up Section - Only show if not "Not Interested" or similar terminal dispositions */}
              {!["connected_not_interested", "wrong_number", "converted", "appointment_booked"].includes(completionDisposition) && (
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="scheduleReFollowUp"
                      checked={scheduleReFollowUp}
                      onChange={(e) => setScheduleReFollowUp(e.target.checked)}
                      className="w-4 h-4 rounded"
                    />
                    <label htmlFor="scheduleReFollowUp" className="text-sm font-medium cursor-pointer">
                      Schedule another follow-up (Callback)
                    </label>
                  </div>

                  {scheduleReFollowUp && (
                    <div className="space-y-3 pl-6 border-l-2 border-cyan-200">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-slate-500">Date</label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start text-left font-normal h-9">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {reFollowUpDate ? format(reFollowUpDate, "dd MMM") : "Select"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={reFollowUpDate}
                                onSelect={setReFollowUpDate}
                                disabled={(date) => date < new Date()}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Time</label>
                          <Select value={reFollowUpTime} onValueChange={setReFollowUpTime}>
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {["09:00", "10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00", "18:00"].map(t => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Input
                        placeholder="Reason (optional)"
                        value={reFollowUpReason}
                        onChange={(e) => setReFollowUpReason(e.target.value)}
                        className="h-9"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowCompleteModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={() => completeFollowUp("completed")}
                  disabled={scheduleReFollowUp && !reFollowUpDate}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {scheduleReFollowUp ? "Complete & Schedule" : "Mark Completed"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Lead Details Modal */}
        <Dialog open={showLeadModal} onOpenChange={setShowLeadModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-cyan-700" />
                Lead Details
              </DialogTitle>
              <DialogDescription>
                View and manage lead information
              </DialogDescription>
            </DialogHeader>

            {leadLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : selectedLead && (
              <div className="space-y-6 mt-4">
                {/* Lead Info */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-lg text-slate-900">{selectedLead.name}</h3>
                  <div className="mt-2 space-y-1 text-sm text-slate-600">
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${selectedLead.phone}`} className="text-cyan-700 hover:underline">{selectedLead.phone}</a>
                    </p>
                    {selectedLead.email && (
                      <p className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4" />
                        {selectedLead.email}
                      </p>
                    )}
                    <p className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize">{selectedLead.source?.replace("_", " ")}</Badge>
                      {selectedLead.city && <Badge variant="secondary">{selectedLead.city}</Badge>}
                      {selectedLead.camp_name && <Badge variant="secondary" className="text-xs">{selectedLead.camp_name}</Badge>}
                    </p>
                    {selectedLead.disposition && (
                      <p className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-slate-500">Current Status:</span>
                        <Badge className="capitalize">{selectedLead.disposition?.replace(/_/g, " ")}</Badge>
                      </p>
                    )}
                  </div>
                </div>

                {/* All Notes History */}
                <div>
                  <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    Notes History ({selectedLead.notes?.length || 0})
                  </h4>
                  <div className="bg-slate-50 rounded-lg p-3 max-h-[200px] overflow-y-auto">
                    {selectedLead.notes && selectedLead.notes.length > 0 ? (
                      <div className="space-y-3">
                        {selectedLead.notes.slice().reverse().map((note, idx) => (
                          <div key={idx} className="bg-white p-3 rounded border border-slate-200">
                            <p className="text-sm text-slate-900 whitespace-pre-wrap">{note.text}</p>
                            <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                              <span className="font-medium">{note.author || 'Unknown'}</span>
                              <span>
                                {note.timestamp || note.created_at ? 
                                  format(new Date(note.timestamp || note.created_at), "dd MMM yyyy, hh:mm a") : 
                                  'No date'
                                }
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-4">No notes yet</p>
                    )}
                  </div>
                </div>

                {/* Add Note */}
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Add Note</h4>
                  <div className="flex gap-2">
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note..."
                      className="flex-1"
                      rows={2}
                    />
                    <Button onClick={addNote} disabled={!newNote.trim()}>
                      Add
                    </Button>
                  </div>
                </div>

                {/* Update Disposition */}
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Update Disposition</h4>
                  <div className="space-y-3">
                    <Select value={newDisposition} onValueChange={setNewDisposition}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select disposition" />
                      </SelectTrigger>
                      <SelectContent>
                        {dispositions.map(d => (
                          <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Textarea
                      value={dispositionNotes}
                      onChange={(e) => setDispositionNotes(e.target.value)}
                      placeholder="Add notes for this disposition update..."
                      rows={2}
                    />
                    <Button 
                      className="w-full bg-cyan-700 hover:bg-cyan-800"
                      onClick={updateDisposition}
                    >
                      Update Disposition
                    </Button>
                  </div>
                </div>

                {/* Schedule New Follow-up */}
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Schedule New Follow-up</h4>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {newFollowUpDate ? format(newFollowUpDate, "PP") : "Pick date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={newFollowUpDate}
                            onSelect={setNewFollowUpDate}
                            disabled={(date) => date < new Date()}
                          />
                        </PopoverContent>
                      </Popover>
                      <Select value={newFollowUpTime} onValueChange={setNewFollowUpTime}>
                        <SelectTrigger>
                          <SelectValue placeholder="Time" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => i + 9).map(hour => (
                            <SelectItem key={hour} value={`${hour}:00`}>{hour}:00</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      placeholder="Follow-up reason..."
                      value={newFollowUpReason}
                      onChange={(e) => setNewFollowUpReason(e.target.value)}
                    />
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={scheduleNewFollowUp}
                      disabled={!newFollowUpDate}
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      Schedule Follow-up
                    </Button>
                  </div>
                </div>

                {/* Activity Timeline */}
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Activity Timeline</h4>
                  <div className="space-y-3 max-h-[200px] overflow-y-auto">
                    {activities.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">No activities yet</p>
                    ) : (
                      activities.map((activity, idx) => (
                        <div key={idx} className="flex gap-3 text-sm">
                          <div className="w-2 h-2 mt-2 rounded-full bg-cyan-500 flex-shrink-0" />
                          <div>
                            <p className="text-slate-900">{activity.description}</p>
                            <p className="text-xs text-slate-500">
                              {activity.created_at && format(new Date(activity.created_at), "dd MMM yyyy, hh:mm a")}
                              {activity.created_by_name && ` • ${activity.created_by_name}`}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reschedule Modal */}
        <Dialog open={showRescheduleModal} onOpenChange={setShowRescheduleModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-cyan-700" />
                Reschedule Follow-up
              </DialogTitle>
              <DialogDescription>
                Change the date and time for this follow-up
              </DialogDescription>
            </DialogHeader>
            
            {followUpToReschedule && (
              <div className="space-y-4 mt-4">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="font-medium text-slate-900">{followUpToReschedule.lead_name}</p>
                  <p className="text-sm text-slate-500">{followUpToReschedule.lead_phone}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Current: {followUpToReschedule.scheduled_date} at {followUpToReschedule.scheduled_time}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">New Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {rescheduleDate ? format(rescheduleDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={rescheduleDate}
                        onSelect={setRescheduleDate}
                        disabled={(date) => date < new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">New Time</label>
                  <Select value={rescheduleTime} onValueChange={setRescheduleTime}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => i + 9).map(hour => (
                        <SelectItem key={hour} value={`${hour}:00`}>{hour}:00</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Reason for Rescheduling (Optional)</label>
                  <Input
                    placeholder="e.g., Customer requested callback later"
                    value={rescheduleReason}
                    onChange={(e) => setRescheduleReason(e.target.value)}
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => {
                    setShowRescheduleModal(false);
                    setRescheduleReason("");
                  }}>
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-cyan-700 hover:bg-cyan-800"
                    onClick={rescheduleFollowUp}
                    disabled={!rescheduleDate}
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    Reschedule
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
